import { Alert, Modal, Spin, Table, Tag, Typography } from 'antd'
import { useState } from 'react'
import { usePaymentRelinkCandidatesQuery, useRelinkQuickBooksPaymentMutation } from './quickBooksResyncQueries'
import { getProposedCandidateId } from './quickBooksResyncModel'
import {
  PaymentRelinkCandidateRow,
  getNoPaymentCandidatesMessage,
  getPaymentRelinkCandidatesErrorMessage,
  getPaymentRelinkErrorMessage,
  getPaymentRelinkSuccessMessage,
  paymentCandidateListTruncatedMessage,
  toPaymentRelinkCandidateRows,
} from './quickBooksPaymentsModel'

const { Paragraph } = Typography

type Props = {
  tenantId: number
  invoiceId: string
  paymentId: string
  paymentTitle: string
  paymentAmount: string
  onClose: () => void
  onRelinked: (message: string) => void
}

const RelinkPaymentModal = ({
  tenantId,
  invoiceId,
  paymentId,
  paymentTitle,
  paymentAmount,
  onClose,
  onRelinked,
}: Props) => {
  const [selectedQboPaymentId, setSelectedQboPaymentId] = useState<string | undefined>(undefined)
  const candidatesQuery = usePaymentRelinkCandidatesQuery(tenantId, invoiceId, paymentId)
  const mutation = useRelinkQuickBooksPaymentMutation()

  const rows = candidatesQuery.isSuccess
    ? toPaymentRelinkCandidateRows(candidatesQuery.data.candidates, candidatesQuery.data.currentQboPaymentId)
    : []
  const selectedId = selectedQboPaymentId ?? getProposedCandidateId(rows)
  const selectedRow = rows.find(row => row.qboPaymentId === selectedId)
  const customerName = candidatesQuery.data?.qboCustomerName ?? ''

  const handleClose = () => {
    if (mutation.isLoading) {
      return
    }
    onClose()
  }

  const handleRelink = () => {
    if (selectedRow === undefined) {
      return
    }
    mutation.mutate(
      { tenantId, invoiceId, paymentId, qboPaymentId: selectedRow.qboPaymentId },
      {
        onSuccess: () => {
          onRelinked(getPaymentRelinkSuccessMessage(paymentTitle, selectedRow.qboPaymentId))
        },
      }
    )
  }

  const columns = [
    { title: 'QBO Payment ID', dataIndex: 'qboPaymentId', key: 'qboPaymentId' },
    { title: 'Ref #', dataIndex: 'reference', key: 'reference' },
    { title: 'Total', dataIndex: 'total', key: 'total' },
    { title: 'Unapplied', dataIndex: 'unappliedAmount', key: 'unappliedAmount' },
    { title: 'Applied to', dataIndex: 'appliedTo', key: 'appliedTo' },
    { title: 'Date', dataIndex: 'transactionDate', key: 'transactionDate' },
    {
      title: 'Matches',
      key: 'matches',
      render: (_: unknown, row: PaymentRelinkCandidateRow) => (
        <>
          {row.matchTags.map(tag => (
            <Tag color="green" key={tag}>{tag}</Tag>
          ))}
          {row.isCurrentLink && <Tag>Current link</Tag>}
          {row.linkedToInvoiceNumber !== null && (
            <Tag color="orange">Linked to a payment on invoice {row.linkedToInvoiceNumber}</Tag>
          )}
        </>
      ),
    },
  ]

  return (
    <Modal
      open
      title={`Relink ${paymentTitle} (${paymentAmount})`}
      okText="Relink"
      cancelText="Cancel"
      width={960}
      onCancel={handleClose}
      onOk={handleRelink}
      closable={!mutation.isLoading}
      maskClosable={!mutation.isLoading}
      keyboard={!mutation.isLoading}
      cancelButtonProps={{ disabled: mutation.isLoading }}
      okButtonProps={{
        disabled: selectedRow === undefined || selectedRow.isCurrentLink || selectedRow.linkedToInvoiceNumber !== null,
      }}
      confirmLoading={mutation.isLoading}
    >
      <Paragraph type="secondary">
        Lists the QuickBooks payments received from {customerName === '' ? "this invoice's customer" : customerName},
        ranked by how well they match {paymentTitle}. Relinking only changes which QuickBooks payment SynkedUP
        points at; it doesn&apos;t change anything in QuickBooks.
      </Paragraph>

      {candidatesQuery.isLoading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin tip="Searching QuickBooks..." />
        </div>
      )}

      {candidatesQuery.isError && (
        <Alert
          type="error"
          showIcon
          message="Failed to look up QuickBooks payments"
          description={getPaymentRelinkCandidatesErrorMessage(candidatesQuery.error)}
        />
      )}

      {candidatesQuery.isSuccess && rows.length === 0 && !candidatesQuery.data.candidateListTruncated && (
        <Alert
          type="warning"
          showIcon
          message="No payments found"
          description={getNoPaymentCandidatesMessage(candidatesQuery.data.qboCustomerName)}
        />
      )}

      {candidatesQuery.isSuccess && candidatesQuery.data.candidateListTruncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Candidate list is incomplete"
          description={paymentCandidateListTruncatedMessage}
        />
      )}

      {rows.length > 0 && (
        <Table
          columns={columns}
          dataSource={rows}
          size="small"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedId === undefined ? [] : [selectedId],
            onChange: selectedRowKeys => setSelectedQboPaymentId(selectedRowKeys[0] as string),
          }}
        />
      )}

      {mutation.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="Relink failed"
          description={getPaymentRelinkErrorMessage(mutation.error)}
        />
      )}
    </Modal>
  )
}

export default RelinkPaymentModal
